/**
 * Redeeming an impersonation handoff at boot.
 *
 * An operator who clicks "Connect as" in the admin portal arrives here with an
 * opaque handoff in the URL fragment. This trades it for a real session —
 * `redeem_impersonation` performs an Auth0 Custom Token Exchange server-side and
 * returns the tokens — which then drives an {@link ImpersonationClient} in place
 * of the Auth0 SPA client.
 *
 * This runs BEFORE the router and before anything checks for a session, because
 * that is the only point where there is no session to conflict with. It is not a
 * route for the same reason: every route in this app hangs off the authenticated
 * shell, and a pre-auth exception carved into that tree would be a permanent
 * invitation to widen.
 *
 * The fragment is consumed the moment it is read — stripped from the address bar
 * before the network call — so a reload cannot replay it, the operator cannot
 * paste it out of the URL bar by accident, and it never appears in a screenshot
 * of the window. It was never sent to a server on the way in either; fragments
 * are not transmitted, logged, or attached to `Referer`.
 */
import type { ImpersonationSessionTokens } from "@authe/fe-commons/session";

/** Fragment parameter the admin portal writes. */
export const HANDOFF_PARAM = "handoff";

/**
 * Fragment parameter carrying the absolute URL to post the handoff to.
 *
 * The backend supplies it because nothing here can build it: redemption happens
 * before any token exists, and the region router derives both the regional host
 * and the `{CONTINENT}` path segment from token claims. With no token it falls
 * back to the bare `api.{root}` host, which carries no base-path mapping for the
 * authentication gateway — so a URL built here would 404.
 */
export const REDEEM_URL_PARAM = "redeem";

/** Path the handoff lands on; not a router route, so it is cleared after use. */
export const HANDOFF_PATH = "/impersonate";

export interface ConsumedHandoff {
  handoff: string;
  /** Absolute redemption URL from the backend; validated before use. */
  redeemUrl: string | null;
}

export interface HandoffRedemption {
  tokens: ImpersonationSessionTokens;
  impersonationId: string;
  /** ISO-8601 — when the session dies. The authorizer enforces it too. */
  expiresAt: string | null;
  accessLevel: "read_only" | "read_write";
}

/** The two bits of browser this needs, injectable so the seam is testable. */
export interface HandoffLocation {
  hash: string;
  pathname: string;
  search: string;
}

export interface HandoffHistory {
  replaceState(data: unknown, unused: string, url: string): void;
}

/**
 * Take the handoff out of the URL, if there is one.
 *
 * Always removes it, even when what follows fails: a handoff is single-use, so
 * one left in the address bar is at best a dead link and at worst something an
 * operator pastes into a ticket.
 */
export function consumeHandoff(
  location: HandoffLocation = window.location,
  history: HandoffHistory = window.history,
): ConsumedHandoff | null {
  const hash = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
  if (!hash) return null;

  const params = new URLSearchParams(hash);
  const handoff = params.get(HANDOFF_PARAM);
  if (!handoff) return null;
  const redeemUrl = params.get(REDEEM_URL_PARAM);

  params.delete(HANDOFF_PARAM);
  params.delete(REDEEM_URL_PARAM);
  const remaining = params.toString();
  // `/impersonate` is a carrier, not a route — the router has no such page, so
  // leaving it in the address bar lands a successful redemption on a 404. Drop
  // it back to the app root; anything else the operator was sent to is kept.
  const pathname = location.pathname === HANDOFF_PATH ? "/" : location.pathname;
  history.replaceState(
    null,
    "",
    `${pathname}${location.search}${remaining ? `#${remaining}` : ""}`,
  );
  return { handoff, redeemUrl };
}

export class HandoffRedemptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HandoffRedemptionError";
  }
}

/**
 * Trade the handoff for a session.
 *
 * Uses `fetch` rather than the shared `ApiClient` on purpose: that client exists
 * to attach a bearer token and this is the one call in the app made by someone
 * who has none. Wiring it through would mean teaching it about a request that
 * must NOT be authenticated, which is a strange thing to teach an API client.
 */
/**
 * Is `url` a redemption endpoint we are willing to post to?
 *
 * The URL arrives in a fragment, so it is caller-supplied even though the
 * backend put it there. Requiring https and a host inside our own API domain
 * keeps a tampered link from turning the handoff into a credential POSTed at
 * somebody else's server.
 */
export function isTrustedRedeemUrl(url: string, apiBaseDomain: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return false;
    return parsed.hostname === apiBaseDomain || parsed.hostname.endsWith(`.${apiBaseDomain}`);
  } catch {
    return false;
  }
}

export async function redeemHandoff(
  handoff: string,
  options: { apiBaseDomain: string; redeemUrl?: string | null; fetchImpl?: typeof fetch },
): Promise<HandoffRedemption> {
  const url = options.redeemUrl ?? "";
  if (!isTrustedRedeemUrl(url, options.apiBaseDomain)) {
    throw new HandoffRedemptionError("This impersonation link is not valid for this environment.");
  }
  const continent = "ANY";
  const doFetch = options.fetchImpl ?? fetch;

  let response: Response;
  try {
    response = await doFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Source-Platform": "web",
        "Source-Region": continent,
        "Destination-Region": continent,
      },
      body: JSON.stringify({ handoff }),
    });
  } catch (cause) {
    throw new HandoffRedemptionError("Could not reach the authentication service.");
  }

  const body = (await response.json().catch(() => null)) as
    | { ok?: boolean; result?: Record<string, unknown>; error?: { message?: string } }
    | null;

  // The envelope carries the real outcome: a refused handoff is `ok: false` with
  // a 4xx `statusCode` inside a 200 response.
  if (!response.ok || !body?.ok || !body.result) {
    throw new HandoffRedemptionError(
      body?.error?.message ?? "This impersonation handoff is no longer valid.",
    );
  }

  const result = body.result;
  const accessToken = typeof result.access_token === "string" ? result.access_token : "";
  const idToken = typeof result.id_token === "string" ? result.id_token : "";
  if (!accessToken || !idToken) {
    // A session missing half its tokens would boot into a confusing half-state;
    // refusing keeps the failure at the point it happened.
    throw new HandoffRedemptionError("The impersonation session came back incomplete.");
  }

  return {
    tokens: {
      access_token: accessToken,
      id_token: idToken,
      expires_in: typeof result.expires_in === "number" ? result.expires_in : undefined,
    },
    impersonationId: String(result.impersonation_id ?? ""),
    expiresAt: typeof result.expires_at === "string" ? result.expires_at : null,
    accessLevel: result.access_level === "read_write" ? "read_write" : "read_only",
  };
}
